import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Mail,
    Lock,
    Eye,
    EyeOff,
    LogIn,
    Shield,
    CheckCircle,
    BookOpen,
    Users,
    BarChart3,
    FileText,
    ArrowRight,
    Globe,
    User
} from 'lucide-react';
import './AdminLogin.css';
import { ApiUrl } from '../API/ApiUrl';

const AdminLogin = () => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });

    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [focusedField, setFocusedField] = useState(null);
    const [capsLockOn, setCapsLockOn] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        if (error) setError('');
    };

    const handleCapsLock = (e) => {
        setCapsLockOn(e.getModifierState("CapsLock"));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.username || !formData.password) {
            setError('Please fill in all fields');
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(`${ApiUrl}/admin/login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.message);
                setIsLoading(false);
                return;
            }

            const expiryTime = Date.now() + 5 * 60 * 60 * 1000; // 5 hours
            // const expiryTime = Date.now() + 2 * 60 * 1000; // 2 minutes

            localStorage.setItem("adminToken", data.token);
            localStorage.setItem("adminTokenExpiry", expiryTime);
            navigate('/admin/dashboard');

        } catch (error) {
            setError("Server error");
        }

        setIsLoading(false);
    };

    return (
        <div className="admin-login">
            {/* Background Pattern */}
            <div className="grid-pattern"></div>

            <div className="login-container">
                {/* Brand Section */}
                <div className="brand-section">
                    <div className="brand-content">
                        <div className="brand-header">
                            <div className="brand-icon">
                                <BookOpen size={32} />
                            </div>
                            <div className="brand-title">
                                <h1>IJACNA</h1>
                                <span>Management System</span>
                            </div>
                        </div>

                        <div className="brand-welcome">
                            <h2>Welcome back,</h2>
                            <p>Sign in to continue to your dashboard</p>
                        </div>

                        <div className="features-grid">
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <FileText size={20} />
                                </div>
                                <div className="feature-info">
                                    <h4>Submission Management</h4>
                                    <p>Review and manage manuscript submissions</p>
                                </div>
                            </div>

                            <div className="feature-card">
                                <div className="feature-icon">
                                    <Users size={20} />
                                </div>
                                <div className="feature-info">
                                    <h4>Reviewer Network</h4>
                                    <p>Coordinate with peer reviewers</p>
                                </div>
                            </div>

                            <div className="feature-card">
                                <div className="feature-icon">
                                    <BarChart3 size={20} />
                                </div>
                                <div className="feature-info">
                                    <h4>Analytics Dashboard</h4>
                                    <p>Track journal performance metrics</p>
                                </div>
                            </div>
                        </div>

                        <div className="login-stats-container">
                            <div className="login-stat-item">
                                <span className="login-stat-value">2,500+</span>
                                <span className="login-stat-label">Submissions</span>
                            </div>
                            <div className="login-stat-divider"></div>
                            <div className="login-stat-item">
                                <span className="login-stat-value">150+</span>
                                <span className="login-stat-label">Reviewers</span>
                            </div>
                            <div className="login-stat-divider"></div>
                            <div className="login-stat-item">
                                <span className="login-stat-value">45+</span>
                                <span className="login-stat-label">Countries</span>
                            </div>
                        </div>

                        <div className="brand-footer">
                            <div className="trust-badges">
                                <div className="trust-badge">
                                    <Shield size={16} />
                                    <span>SSL Secured</span>
                                </div>
                                <div className="trust-badge">
                                    <CheckCircle size={16} />
                                    <span>ISO Certified</span>
                                </div>
                                <div className="trust-badge">
                                    <Globe size={16} />
                                    <span>Global Access</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Login Section */}
                <div className="login-section">
                    <div className="login-card">
                        <div className="login-header">
                            <h3>Sign In</h3>
                            <p>Enter your credentials to access admin panel</p>
                        </div>

                        {error && (
                            <div className="error-message">
                                <span className="error-icon">⚠</span>
                                <span>{error}</span>
                            </div>
                        )}
                       

                        <form onSubmit={handleSubmit} className="login-form">
                            <div className={`input-group ${focusedField === 'username' ? 'focused' : ''}`}>
                                <label htmlFor="username">
                                    <User size={16} />
                                    <span>Username <span className="required">*</span> </span>
                                </label>
                                <div className="input-wrapper">
                                    <input
                                        type="text"
                                        id="username"
                                        name="username"
                                        value={formData.username}

                                        onChange={handleChange}
                                        onFocus={() => setFocusedField('username')}
                                        onBlur={() => setFocusedField(null)}
                                        placeholder="Enter your username"
                                        disabled={isLoading}
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <div className={`input-group ${focusedField === 'password' ? 'focused' : ''}`}>
                                <label htmlFor="password">
                                    <Lock size={16} />
                                    <span>Password <span className="required">*</span> </span>
                                </label>
                                <div className="input-wrapper">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        name="password"
                                        value={formData.password}
                                        onKeyUp={handleCapsLock}
                                        onKeyDown={handleCapsLock}
                                        onChange={handleChange}
                                        onFocus={() => setFocusedField('password')}
                                        onBlur={() => {
                                            setFocusedField(null);
                                            setCapsLockOn(false); // optional reset
                                        }} placeholder="Enter your password"
                                        disabled={isLoading}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                        disabled={isLoading}
                                    >
                                        {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                                    </button>
                                </div>
                            </div>

                             {capsLockOn && (
                            <p className="caps-warning">⚠ Caps Lock is ON</p>
                        )}

                            <div className="form-footer">
                                <label className="checkbox-label">
                                    {/* <input type="checkbox" />
                                    <span className="checkbox-custom"></span>
                                    <span className="checkbox-text">Remember me</span> */}
                                </label>
                                <a href="#" className="forgot-link">Forgot password?</a>
                            </div>

                            <button
                                type="submit"
                                className={`login-button ${isLoading ? 'loading' : ''}`}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner"></span>
                                        <span>Authenticating...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Sign In</span>
                                        <LogIn size={18} />
                                    </>
                                )}
                            </button>
                        </form>

                        {/* <div className="login-footer">
                            <p>Need help? <a href="#">Contact support</a></p>
                        </div> */}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;